/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable sonarjs/prefer-immediate-return */
import { createAsyncThunk } from '@reduxjs/toolkit'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import {
  ProductInventoryStatusNotesType,
  ProductInventoryStatusType,
} from 'features/Product/@types/Product'
import {
  getInventoryLocation,
  GetInventoryLocationResponseType,
  postInventoryLocation,
  PostInventoryLocationPayloadType,
  postInventoryTransferByAsset,
  postInventoryTransferBySku,
  getEligibleRack,
  getEligibleAssets,
} from 'features/Product/services/inventoryDetail'
import { getScanPackageId } from 'features/Product/services/packageId'
import {
  getProductInventoryStatus,
  getInventoryStatusNotes,
} from 'features/Product/services/product'
import {
  getInventoriesFeatureFlag,
  GetInventoriesFeatureFlagResponseType,
} from 'features/Product/services/stockDetail'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'

export const SLICE_NAME = 'product/inventoryDetail'

export const fetchGetInventoryLocation = createAsyncThunk(
  `${SLICE_NAME}/fetchGetInventoryLocation`,
  async (
    params: Parameters<typeof getInventoryLocation>[0] | undefined,
    { rejectWithValue, getState },
  ) => {
    try {
      const { inventoryDetail } = getState() as StoreStateType

      const qs = { ...inventoryDetail.filter, ...params }

      const cleanQs = Object.fromEntries(
        Object.entries(qs).filter(([_, v]) => v !== '' && v != null),
      )

      const res = await getInventoryLocation({
        ...cleanQs,
        have_stock: true,
      })

      const result: GetInventoryLocationResponseType = {
        data: res.data.data,
        pagination: res.data.pagination,
      }

      return result
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetInventoryLocationList = createAsyncThunk(
  `${SLICE_NAME}/fetchGetInventoryLocationList`,
  async (params: Parameters<typeof getInventoryLocation>[0], { rejectWithValue, getState }) => {
    try {
      const { inventoryDetail } = getState() as StoreStateType

      const { product_ids: productIdParams, ...restParams } = params || {}
      const { product_ids: productIdFilter, ...filter } = inventoryDetail.filter
      const qs = { ...filter, ...restParams }

      const cleanQs = Object.fromEntries(
        Object.entries(qs).filter(([_, v]) => v !== '' && v != null),
      )

      const res = await getInventoryLocation({
        ...cleanQs,
        have_stock: true,
        'pagination.page_size': 50,
      })

      const result: GetInventoryLocationResponseType = {
        data: res.data.data,
        pagination: res.data.pagination,
      }

      return result
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPostInventoryLocation = createAsyncThunk(
  `${SLICE_NAME}/fetchPostInventoryLocation`,
  async (
    arg: {
      payload: PostInventoryLocationPayloadType
      callback?: () => void
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { payload, callback } = arg
      const res = await postInventoryLocation(payload)

      dispatch(fetchGetInventoryLocation())
      toastSuccess(`Product detail berhasil di tambahkan`)

      if (callback) callback()

      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetProductInventoryStatus = createAsyncThunk(
  `${SLICE_NAME}/fetchGetProductInventoryStatus`,
  async (_, { rejectWithValue }) => {
    try {
      const res = await getProductInventoryStatus()
      const { status } = res.data.data

      return status as ProductInventoryStatusType[]
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetProductInventoryStatusNotes = createAsyncThunk(
  `${SLICE_NAME}/fetchGetProductInventoryStatusNotes`,
  async (_, { getState, rejectWithValue }) => {
    try {
      const { inventoryDetail } = getState() as StoreStateType
      const { constants } = inventoryDetail

      if (!constants.status.length) return []

      const listStatusId = constants.status.map((statusItem) => statusItem.id)

      const results = await Promise.all(
        listStatusId.map((id) =>
          getInventoryStatusNotes({
            page_size: 100,
            page_index: 1,
            inventory_status_id: id,
            exclusion_preset: 0,
          }).then((responseNotes) =>
            responseNotes.data.data.map((item) => ({ ...item, status_id: id })),
          ),
        ),
      )
      return results.flat()
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetInventoriesFeatureFlag = createAsyncThunk(
  `${SLICE_NAME}/fetchGetInventoriesFeatureFlag`,
  async (_, { getState, rejectWithValue }) => {
    try {
      const {
        inventoryDetail: {
          query: { location_id, product_id },
        },
      } = getState() as StoreStateType

      const res = await getInventoriesFeatureFlag({
        product_id,
        location_id,
      })

      const data = res.data.data as GetInventoriesFeatureFlagResponseType

      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetScanPackageId = createAsyncThunk(
  `${SLICE_NAME}/fetchGetScanPackageId`,
  async (packageLabel: string) => {
    try {
      const res = await getScanPackageId(packageLabel)
      return res.data.data
    } catch (error) {
      callErrorMsg(error)
      return null
    }
  },
)

export const fetchPostInventoryTransferBySku = createAsyncThunk(
  `${SLICE_NAME}/fetchPostInventoryTransferBySku`,
  async (
    arg: {
      payload: Parameters<typeof postInventoryTransferBySku>[0]
      callback?: () => void
    },
    { dispatch },
  ) => {
    try {
      const { payload, callback } = arg

      const res = await postInventoryTransferBySku(payload)

      toastSuccess('Status inventory berhasil diubah')

      dispatch(fetchGetInventoryLocation())

      if (callback) callback()

      return res.data.data
    } catch (error) {
      callErrorMsg(error)
      return null
    }
  },
)

export const fetchPostInventoryTransferByAsset = createAsyncThunk(
  `${SLICE_NAME}/fetchPostInventoryTransferByAsset`,
  async (
    arg: {
      payload: Parameters<typeof postInventoryTransferByAsset>[0]
      callback?: () => void
    },
    { dispatch },
  ) => {
    try {
      const { payload, callback } = arg

      const res = await postInventoryTransferByAsset(payload)

      dispatch(fetchGetInventoryLocation())

      toastSuccess('Pallet berhasil dipindahkan')

      if (callback) callback()

      return res.data.data
    } catch (error) {
      callErrorMsg(error)
      return null
    }
  },
)

export const fetchGetEligibleRack = createAsyncThunk(
  `${SLICE_NAME}/fetchGetEligibleRack`,
  async (params: Parameters<typeof getEligibleRack>[0], { rejectWithValue }) => {
    try {
      const res = await getEligibleRack(params)

      return res.data.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetEligibleAssets = createAsyncThunk(
  `${SLICE_NAME}/fetchGetEligibleAssets`,
  async (params: Parameters<typeof getEligibleAssets>[0], { rejectWithValue }) => {
    try {
      const res = await getEligibleAssets(params)

      return res.data.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)
